import type { GridValidRowModel } from '@/ui/primitives';

type ForcedLoadState = 'loading' | 'empty' | 'error' | null;

export type ServerDataTableViewState<T extends GridValidRowModel> = {
  effectiveErrorMessage: string | null;
  effectiveRowCount: number;
  effectiveRowsData: T[];
  forcedLoadState: ForcedLoadState;
  gridReadyState: 'loading' | 'placeholder' | 'empty' | 'error' | 'ready';
  showPlaceholderGrid: boolean;
  showSkeletonGrid: boolean;
  skeletonTotalRowCount: number | undefined;
};

const resolveForcedLoadState = (value: string | null): ForcedLoadState => {
  if (value === 'loading' || value === 'empty' || value === 'error') {
    return value;
  }
  return null;
};

export const resolveServerDataTableViewState = <T extends GridValidRowModel>(input: {
  errorMessage: string | null;
  externalLoading: boolean;
  forcedLoadStateParam: string | null;
  queryIsFetching: boolean;
  rowCount: number;
  rowsData: T[];
  showFetchSkeleton: boolean;
  showInitialSkeleton: boolean;
  showPaginationSkeleton: boolean;
  showPaginationSkeletonWhenRowsVisible: boolean;
  showSkeletonOnFetch: boolean;
}): ServerDataTableViewState<T> => {
  const forcedLoadState = resolveForcedLoadState(input.forcedLoadStateParam);
  const forcedErrorMessage =
    forcedLoadState === 'error' ? 'Failed to load data (forced by loadState query param).' : null;
  const effectiveErrorMessage = forcedErrorMessage ?? input.errorMessage;
  const hasForcedNoRows = forcedLoadState === 'empty' || forcedLoadState === 'error';
  const effectiveRowsData = hasForcedNoRows ? [] : input.rowsData;
  const effectiveRowCount = hasForcedNoRows ? 0 : input.rowCount;
  const hasVisibleRows = effectiveRowsData.length > 0;
  const showQueryLoadingSkeleton =
    !hasForcedNoRows && !hasVisibleRows && (input.queryIsFetching || input.externalLoading);
  const showPaginationLoadingSkeleton =
    !hasForcedNoRows &&
    input.showPaginationSkeleton &&
    (input.showPaginationSkeletonWhenRowsVisible || !hasVisibleRows);
  const shouldShowFetchSkeleton = input.showSkeletonOnFetch
    ? !hasVisibleRows && (input.showPaginationSkeleton || input.showFetchSkeleton)
    : false;
  const showSkeletonGrid =
    forcedLoadState === 'loading'
      ? true
      : showPaginationLoadingSkeleton
        ? true
        : !hasForcedNoRows && shouldShowFetchSkeleton
          ? true
          : showQueryLoadingSkeleton
            ? true
            : input.showInitialSkeleton && !hasForcedNoRows && !hasVisibleRows;
  const gridReadyState = showSkeletonGrid
    ? 'loading'
    : hasForcedNoRows
      ? forcedLoadState
      : effectiveErrorMessage
        ? 'error'
        : 'ready';

  return {
    effectiveErrorMessage,
    effectiveRowCount,
    effectiveRowsData,
    forcedLoadState,
    gridReadyState,
    showPlaceholderGrid: false,
    showSkeletonGrid,
    skeletonTotalRowCount: showSkeletonGrid ? undefined : effectiveRowCount,
  };
};
